import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { idOrSlugColumn } from '@/lib/idOrSlug';
import { Layout } from '@/components/layout/Layout';
import { PropertyImageGallery } from '@/components/property-detail/PropertyImageGallery';
import { PropertyContactSidebar } from '@/components/property-detail/PropertyContactSidebar';
import { PropertyDetailsSection } from '@/components/property-detail/PropertyDetailsSection';
import { PropertyCard } from '@/components/property/PropertyCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useState, useEffect } from 'react';
import { trackView } from '@/lib/tracking';
import {
  MapPin, Maximize, Heart, Share2, Printer,
  ChevronRight, Home, Eye, Calendar, Copy, Store, Building2,
  ParkingCircle, Truck
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { MobileContactActionBar } from '@/components/property-detail/mobile/MobileContactActionBar';
import { MobileSectionTabs } from '@/components/property-detail/mobile/MobileSectionTabs';
import { MobileLeadFormSection } from '@/components/property-detail/mobile/MobileLeadFormSection';
import { MobileSafetyTips, MobileUsefulLinks, MobileReportListing } from '@/components/property-detail/mobile/MobileBottomBlocks';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice } from '@/lib/formatPrice';

const commercialSubTypes: Record<string, string> = {
  office: 'Office Space',
  retail: 'Retail / Shop',
  warehouse: 'Warehouse',
  showroom: 'Showroom',
  hotel: 'Hotel / Hospitality',
  'mixed-use': 'Mixed-Use',
};

export default function CommercialDetail() {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const lang = i18n.language || 'en';
  const [saved, setSaved] = useState(false);

  const { data: property, isLoading } = useQuery({
    queryKey: ['commercial-property', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('*, property_images(*)')
        .eq(idOrSlugColumn(id), id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (!property?.id) return;
    trackView({ listingType: 'commercial', listingId: property.id, city: property.city });
  }, [property?.id]);

  const { data: similarProperties } = useQuery({
    queryKey: ['similar-commercial', property?.city, id],
    queryFn: async () => {
      const { data } = await supabase
        .from('properties')
        .select('*, property_images(*)')
        .eq('property_type', 'commercial')
        .eq('status', 'active')
        .eq('approval_status', 'approved')
        .eq('city', property!.city)
        .neq('id', id!)
        .limit(4);
      return data || [];
    },
    enabled: !!property,
  });

  const handleSave = async () => {
    if (!user) {
      toast({ variant: 'destructive', title: 'Please login to save properties' });
      return;
    }
    if (saved) {
      await supabase.from('favorites').delete().eq('property_id', property!.id).eq('user_id', user.id);
      setSaved(false);
      toast({ title: 'Removed from saved' });
    } else {
      await supabase.from('favorites').insert({ property_id: property!.id, user_id: user.id });
      setSaved(true);
      toast({ title: 'Property saved!' });
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: 'Commercial Property', url });
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied to clipboard!' });
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-6 space-y-6">
          <Skeleton className="h-[460px] rounded-xl" />
          <div className="grid md:grid-cols-[1fr_360px] gap-6">
            <Skeleton className="h-60" />
            <Skeleton className="h-[500px]" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-4">Property not found</h1>
          <Link to="/commercial" className="text-primary hover:underline">Browse commercial properties</Link>
        </div>
      </Layout>
    );
  }

  const getName = (val: any) => typeof val === 'object' ? (val?.[lang] || val?.en || '') : (val || '');
  const title = getName(property.title);
  const description = getName(property.description);
  const images = (property.property_images || []).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (price: number, currency: string) => fmtPrice(price, currency, displayCurrency, rate);
  const timeAgo = property.created_at ? formatDistanceToNow(new Date(property.created_at), { addSuffix: true }) : '';
  const features = property.features as any;
  const subType = features?.commercial_sub_type;

  return (
    <Layout>
      <SEOHead
        title={`${title} — Commercial Property in ${property.city}`}
        description={`Commercial ${subType ? commercialSubTypes[subType] || subType : 'property'} for ${property.listing_type} in ${property.city}. ${property.area_sqm ? property.area_sqm + ' m². ' : ''}${formatPrice(property.price, property.currency || 'ETB')}`}
        canonical={`/commercial/${id}`}
        ogType="article"
        ogImage={{ type: 'property', id: id!, badge: 'Commercial' }}
      />
      {/* Breadcrumb */}
      <div className="bg-muted/50 border-b">
        <div className="container py-2.5">
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground overflow-x-auto">
            <Link to="/" className="hover:text-primary transition-colors flex items-center gap-1 shrink-0">
              <Home className="h-3 w-3" />Home
            </Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <Link to="/commercial" className="hover:text-primary transition-colors shrink-0">Commercial</Link>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <span className="text-foreground truncate font-medium">{title}</span>
          </nav>
        </div>
      </div>

      {/* ================= DESKTOP LAYOUT (≥lg) ================= */}
      <div className="hidden lg:block">
        <div className="container py-6">
          <PropertyImageGallery images={images} title={title} isFeatured={property.is_featured} />

          {/* Header */}
          <div className="mt-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className="text-xs capitalize">{property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}</Badge>
                  <Badge variant="outline" className="text-xs">Commercial</Badge>
                  {subType && <Badge variant="secondary" className="text-xs">{commercialSubTypes[subType] || subType}</Badge>}
                  {property.is_featured && <Badge variant="secondary" className="text-xs">Featured</Badge>}
                </div>

                <div className="flex items-baseline gap-2 mb-2 flex-wrap min-w-0">
                  <span className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground break-words">
                    {formatPrice(property.price, property.currency || 'ETB')}
                  </span>
                  {property.listing_type === 'rent' && <span className="text-base sm:text-lg text-muted-foreground">/month</span>}
                </div>

                <div className="flex items-start gap-1.5 text-muted-foreground mb-3 min-w-0">
                  <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm break-words min-w-0">
                    {property.city}{property.sub_city ? ` - ${property.sub_city}` : ''}{property.woreda ? `, ${property.woreda}` : ''}
                    {property.address ? `, ${property.address}` : ''}
                  </span>
                </div>

                <div className="flex items-center gap-x-6 gap-y-2 text-sm text-muted-foreground flex-wrap min-w-0">
                  {property.area_sqm != null && (
                    <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4" /><strong className="text-foreground">{property.area_sqm}</strong> m²</span>
                  )}
                  {property.total_floors != null && (
                    <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /><strong className="text-foreground">{property.total_floors}</strong> Floors</span>
                  )}
                  {property.has_parking && (
                    <span className="flex items-center gap-1.5"><ParkingCircle className="h-4 w-4" /><strong className="text-foreground">Parking</strong></span>
                  )}
                  {features?.loading_dock && (
                    <span className="flex items-center gap-1.5"><Truck className="h-4 w-4" /><strong className="text-foreground">Loading Dock</strong></span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleSave} className={saved ? 'text-red-500 border-red-200' : ''}>
                  <Heart className={`h-4 w-4 sm:mr-1.5 ${saved ? 'fill-red-500' : ''}`} />
                  <span className="hidden sm:inline">{saved ? 'Saved' : 'Save'}</span>
                </Button>
                <Button variant="outline" size="sm" onClick={handleShare}>
                  <Share2 className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Share</span>
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Print</span>
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-muted-foreground flex-wrap">
              {timeAgo && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Added {timeAgo}</span>}
              {property.view_count != null && <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{property.view_count} views</span>}
              <span className="flex items-center gap-1 font-mono"><Copy className="h-3 w-3" />ID: {(property.reference_code || `ID${property.id?.slice(0, 8)}`).toUpperCase()}</span>
            </div>
          </div>

          <Separator className="mb-6" />

          <div className="grid lg:grid-cols-[1fr_360px] gap-6">
            <PropertyDetailsSection property={property} title={title} description={description} isPlot={false} />
            <div className="order-first lg:order-last">
              <div className="lg:sticky lg:top-[100px]">
                <PropertyContactSidebar property={property} propertyId={id!} />
              </div>
            </div>
          </div>

          {similarProperties && similarProperties.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display text-xl font-bold mb-6">Similar Commercial Properties</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {similarProperties.map((p: any) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= MOBILE / TABLET LAYOUT (<lg) ================= */}
      <div className="lg:hidden pb-20">
        <PropertyImageGallery images={images} title={title} isFeatured={property.is_featured} />

        {/* Compact header */}
        <div className="container px-4 pt-4 pb-3">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="font-display text-2xl font-bold text-foreground break-words min-w-0">
              {formatPrice(property.price, property.currency || 'ETB')}
              {property.listing_type === 'rent' && <span className="text-sm text-muted-foreground font-normal">/month</span>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={handleSave} aria-label="Save" className={`h-9 w-9 rounded-full border flex items-center justify-center ${saved ? 'text-red-500 border-red-200 bg-red-50' : 'text-muted-foreground'}`}>
                <Heart className={`h-4 w-4 ${saved ? 'fill-red-500' : ''}`} />
              </button>
              <button onClick={handleShare} aria-label="Share" className="h-9 w-9 rounded-full border flex items-center justify-center text-muted-foreground">
                <Share2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className="text-[10px] capitalize">{property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}</Badge>
            <Badge variant="outline" className="text-[10px]">Commercial</Badge>
            {subType && <Badge variant="secondary" className="text-[10px]">{commercialSubTypes[subType] || subType}</Badge>}
          </div>

          <div className="flex items-start gap-1.5 text-muted-foreground mb-3">
            <MapPin className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <span className="text-xs break-words">
              {property.city}{property.sub_city ? ` - ${property.sub_city}` : ''}{property.woreda ? `, ${property.woreda}` : ''}
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
            {property.area_sqm != null && (
              <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4" /><strong className="text-foreground">{property.area_sqm}</strong> m²</span>
            )}
            {property.total_floors != null && (
              <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /><strong className="text-foreground">{property.total_floors}</strong> floors</span>
            )}
            {property.has_parking && (
              <span className="flex items-center gap-1.5"><ParkingCircle className="h-4 w-4" /><strong className="text-foreground">Parking</strong></span>
            )}
          </div>
        </div>

        <div className="container px-4 pb-3">
          <MobileContactActionBar
            phone={property.contact_phone || '+251911000000'}
            email={property.contact_email}
            variant="inline"
          />
        </div>

        <MobileSectionTabs
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'nearby', label: 'Nearby' },
            { id: 'home-finance', label: 'Home Finance' },
            { id: 'price-index', label: 'Price Index' },
          ]}
        />

        <div className="container px-4 pt-2">
          <PropertyDetailsSection
            property={property}
            title={title}
            description={description}
            isPlot={false}
            mobileLayout
          />

          <div className="mt-8">
            <MobileLeadFormSection listingId={id!} listingLabel="commercial property" referenceCode={property.reference_code} property={property} />
          </div>

          <div className="mt-6 space-y-4">
            <MobileSafetyTips />
            <MobileUsefulLinks
              links={[
                { label: `Commercial in ${property.city}`, to: `/commercial?city=${property.city}` },
                { label: `Properties for sale in ${property.city}`, to: `/buy?city=${property.city}` },
                { label: `Properties for rent in ${property.city}`, to: `/rent?city=${property.city}` },
              ]}
            />
            <MobileReportListing targetType="property" targetId={id!} label="Report this property" />
          </div>

          {similarProperties && similarProperties.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display text-lg font-bold mb-4">Similar Commercial</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {similarProperties.map((p: any) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
            </div>
          )}
        </div>

        <MobileContactActionBar
          phone={property.contact_phone || '+251911000000'}
          email={property.contact_email}
          variant="fixed"
        />
      </div>
    </Layout>
  );
}
